// src/pages/gameweeks/GameweekManagement.tsx
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { Plus, RefreshCw, Download } from "lucide-react";
import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Layout from "@/components/Layout";
import { toast } from "react-toastify";
import GameweekTable from "@/pages/gameweeks/GameweekTable";
import GameweekForm from "@/pages/gameweeks/GameweekForm";
import {
  useGameweeks,
  useCreateGameweek,
  useUpdateGameweek,
  useOpenGameweek,
  useLockGameweek,
  useCalculateGameweekPoints,
  useDeleteGameweek,
  useAssignMatches,
  useForceRecalculate,
  useResyncTeams,
} from "@/hooks/api/useGameweeks";
import type {
  Gameweek,
  CreateGameweekRequest,
  UpdateGameweekRequest,
} from "@/types/api.types";

export default function GameweekManagement() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentGameweek, setCurrentGameweek] =
    useState<Partial<Gameweek> | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const { data: gameweeksData, isLoading, error, refetch, isFetching } = useGameweeks();
  const gameweeks: Gameweek[] = Array.isArray(gameweeksData) ? gameweeksData : [];
  const { mutate: createGameweek, isPending: isCreating } = useCreateGameweek();
  const { mutate: updateGameweek, isPending: isUpdating } = useUpdateGameweek();
  const { mutate: openGameweek } = useOpenGameweek();
  const { mutate: lockGameweek } = useLockGameweek();
  const { mutate: calculatePoints } = useCalculateGameweekPoints();
  const { mutate: deleteGameweek, isPending: isDeleting } = useDeleteGameweek();
  const { mutate: assignMatches, isPending: isAssigning } = useAssignMatches();
  const { mutate: forceRecalculate, isPending: isRecalculating } = useForceRecalculate();
  const { mutate: resyncTeams, isPending: isResyncing } = useResyncTeams();

  // Check if we can create a new gameweek
  const canCreateNewGameweek = () => {
    if (!gameweeks || gameweeks.length === 0) {
      return true; // Allow creating the first gameweek
    }

    // Check if there's any gameweek that is not COMPLETED
    const hasIncompleteGameweek = gameweeks.some(
      (gw) => gw.status !== "COMPLETED"
    );

    return !hasIncompleteGameweek;
  };

  const handleAdd = () => {
    if (!canCreateNewGameweek()) {
      toast.error(
        "Cannot create a new gameweek. Please complete all existing gameweeks first (status must be COMPLETED)."
      );
      return;
    }

    setIsEditing(false);
    setCurrentGameweek(null);
    setIsOpen(true);
  };

  const handleEdit = (gw: Gameweek) => {
    setIsEditing(true);
    setCurrentGameweek(gw);
    setIsOpen(true);
  };

  const handleSave = (
    formData: CreateGameweekRequest | UpdateGameweekRequest,
  ) => {
    if (isEditing && currentGameweek?.id) {
      updateGameweek(
        { id: currentGameweek.id, data: formData as UpdateGameweekRequest },
        {
          onSuccess: () => {
            toast.success("Gameweek updated");
            setIsOpen(false);
            setCurrentGameweek(null);
          },
          onError: (e: any) =>
            toast.error(e.response?.data?.message || "Failed to update"),
        },
      );
    } else {
      createGameweek(formData as CreateGameweekRequest, {
        onSuccess: () => {
          toast.success("Gameweek created");
          setIsOpen(false);
        },
        onError: (e: any) =>
          toast.error(e.response?.data?.message || "Failed to create"),
      });
    }
  };

  const handleOpen = (id: number) => {
    if (
      !confirm(
        "Open this gameweek? All base teams will be cloned for selection.",
      )
    )
      return;
    openGameweek(id, {
      onSuccess: () =>
        toast.success("Gameweek opened — users can now select teams"),
      onError: (e: any) =>
        toast.error(e.response?.data?.message || "Failed to open"),
    });
  };

  const handleLock = (id: number, name: string) => {
    if (
      !confirm(`Lock "${name}"? Users will not be able to change their teams.`)
    )
      return;
    lockGameweek(id, {
      onSuccess: () => toast.success("Gameweek locked"),
      onError: (e: any) =>
        toast.error(e.response?.data?.message || "Failed to lock"),
    });
  };

  const handleCalculate = (id: number, name: string) => {
    if (
      !confirm(
        `Calculate points for "${name}"? This will finalize scores and mark the gameweek as completed.`,
      )
    )
      return;
    calculatePoints(id, {
      onSuccess: () => toast.success("Points calculated — gameweek completed!"),
      onError: (e: any) =>
        toast.error(e.response?.data?.message || "Failed to calculate"),
    });
  };

  const handleAssignMatches = (id: number, matchIds: number[]) => {
    if (matchIds.length === 0) return;
    assignMatches(
      { id, matchIds },
      {
        onSuccess: (data: any) =>
          toast.success(
            `${data.assigned ?? matchIds.length} match(es) assigned to gameweek successfully`
          ),
        onError: (e: any) =>
          toast.error(e.response?.data?.message || "Failed to assign matches"),
      }
    );
  };

  const handleForceRecalculate = (id: number, name: string) => {
    if (
      !confirm(
        `Force-recalculate points for "${name}"?\n\nThis will re-read all assigned match events and overwrite stored player stats/points. Use this to fix corrupted data.\n\nMake sure all matches are assigned to this gameweek first.`
      )
    )
      return;
    forceRecalculate(id, {
      onSuccess: (data: any) =>
        toast.success(
          `"${name}" recalculated — ${data.matchesProcessed ?? 0} match(es) processed`
        ),
      onError: (e: any) =>
        toast.error(
          e.response?.data?.message || "Force-recalculate failed"
        ),
    });
  };

  const handleResyncTeams = (id: number, name: string) => {
    if (
      !confirm(
        `Re-sync team snapshots for "${name}"?\n\nThis will DELETE all current cloned teams for this gameweek and re-clone them from base teams — WITHOUT auto-replacing eliminated players.\n\nUse this to fix squads that were incorrectly auto-replaced when the gameweek was opened.`
      )
    )
      return;
    resyncTeams(id, {
      onSuccess: (data: any) =>
        toast.success(
          data.message ?? `"${name}" team snapshots re-synced successfully`
        ),
      onError: (e: any) =>
        toast.error(e.response?.data?.message || "Re-sync failed"),
    });
  };

  const handleDelete = (id: number) => {
    if (!confirm("Delete this gameweek? This cannot be undone.")) return;
    deleteGameweek(id, {
      onSuccess: () => toast.success("Gameweek deleted"),
      onError: (e: any) =>
        toast.error(e.response?.data?.message || "Failed to delete"),
    });
  };

  if (error) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64">
          <p className="text-red-600 mb-4">Failed to load gameweeks</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Gameweek Management
          </h1>
          <p className="text-gray-500 mt-1">
            Manage team selection windows for each round of FIFA World Cup 2026
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const csv = await apiClient.get(
                    API_ENDPOINTS.GAMEWEEKS.EXPORT_ALL_CSV,
                    { responseType: "text" },
                  );
                  const blob = new Blob([csv as unknown as string], {
                    type: "text/csv;charset=utf-8",
                  });
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = `all-gameweeks-${new Date()
                    .toISOString()
                    .slice(0, 10)}.csv`;
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success("Full export downloaded");
                } catch (e: any) {
                  toast.error(
                    e?.response?.data?.message || "Failed to export",
                  );
                }
              }}
              title="Export every gameweek's teams as CSV"
            >
              <Download className="h-4 w-4 mr-1.5" /> Export All
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                refetch().then(() => toast.success("Refreshed"));
              }}
              disabled={isFetching}
              title="Refresh gameweeks"
            >
              <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            </Button>
            <Button
              onClick={handleAdd}
              className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isCreating || !canCreateNewGameweek()}
            >
              <Plus className="mr-2 h-4 w-4" /> Create Gameweek
            </Button>
          </div>
          {!canCreateNewGameweek() && (
            <p className="text-xs text-amber-600">
              Complete existing gameweeks first
            </p>
          )}
        </div>
      </div>

      {/* Stats */}
      {gameweeks && gameweeks.length > 0 && (
        <div className="flex items-center gap-2 text-xs mb-4">
          <Badge className="bg-gray-100 text-gray-700 font-medium">{gameweeks.length} Total</Badge>
          <Badge className="bg-gray-200 text-gray-600 font-medium">{gameweeks.filter((g) => g.status === "UPCOMING").length} Upcoming</Badge>
          <Badge className="bg-green-100 text-green-700 font-medium">{gameweeks.filter((g) => g.status === "OPEN").length} Open</Badge>
          <Badge className="bg-orange-100 text-orange-700 font-medium">{gameweeks.filter((g) => g.status === "LOCKED").length} Locked</Badge>
          <Badge className="bg-blue-100 text-blue-700 font-medium">{gameweeks.filter((g) => g.status === "COMPLETED").length} Completed</Badge>
        </div>
      )}

      <GameweekTable
        gameweeks={gameweeks || []}
        isLoading={isLoading}
        onEdit={handleEdit}
        onOpen={handleOpen}
        onLock={handleLock}
        onCalculate={handleCalculate}
        onDelete={handleDelete}
        isDeleting={isDeleting}
        onAssignMatches={handleAssignMatches}
        isAssigning={isAssigning}
        onForceRecalculate={handleForceRecalculate}
        isRecalculating={isRecalculating}
        onResyncTeams={handleResyncTeams}
        isResyncing={isResyncing}
      />

      <GameweekForm
        open={isOpen}
        onOpenChange={setIsOpen}
        gameweek={currentGameweek}
        isEditing={isEditing}
        onSave={handleSave}
        isSaving={isCreating || isUpdating}
      />
    </Layout>
  );
}
